import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Server, Code, HardDrive, Mail, Users, Rocket, Globe, Shield, Activity, Copy } from 'lucide-react';
import { useState } from 'react';
import Button from '@/components/atoms/Button';
import Badge from '@/components/atoms/Badge';
import StatCounter from '@/components/molecules/StatCounter';
import FeatureTile from '@/components/molecules/FeatureTile';
import LedgerCard from '@/components/molecules/LedgerCard';
import CodeSnippet from '@/components/molecules/CodeSnippet';
import MarketingLayout from '@/layouts/MarketingLayout';
import HeroBackground from '@/components/organisms/HeroBackground';

// Mock seals for §3.1.4
const mockSeals = [
  { hash: 'a7c3f91d2e8b4a6c5d0f7e1b3a9c8d2e', type: 'sov_customer_created', timestamp: '2 min ago' },
  { hash: 'b8d4e02f3f9c5b7d6e1a8f2c4b0d9e3f', type: 'ast_bucket_sealed', timestamp: '5 min ago' },
  { hash: 'c9e5f13a4a0d6c8e7f2b9a3d5c1e0f4a', type: 'scn_mesh_audit', timestamp: '8 min ago' },
  { hash: 'd0f6a24b5b1e7d9f8a3c0b4e6d2f1a5b', type: 'ops_deploy_complete', timestamp: '11 min ago' },
  { hash: 'e1a7b35c6c2f8e0a9b4d1c5f7e3a2b6c', type: 'sov_attestation_signed', timestamp: '14 min ago' },
  { hash: 'f2b8c46d7d3a9f1b0c5e2d6a8f4b3c7d', type: 'ast_migration_sealed', timestamp: '17 min ago' },
];

const HomePage = () => {
  const [copied, setCopied] = useState(false);
  const copyCmd = () => { navigator.clipboard.writeText('npx drklynx init my-project'); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <MarketingLayout>
      <Helmet>
        <title>DrkLynX — Your Digital Future, Quantum-Proof.</title>
        <meta name="description" content="Sovereign hosting, end-to-end Vicode (DrkLynX's Verifiable-Code build model), and cryptographic truth for the post-quantum internet. Operated by a sovereign architect, powered by ~300,000 lines of agentic code under a Post-Quantum Ready Architecture (Phase A HMAC-SHA256 live; Phase B ML-DSA-65 / NIST FIPS-204 primitive swap in flight)." />
        <meta property="og:title" content="DrkLynX — Your Digital Future, Quantum-Proof." />
        <meta property="og:description" content="The sovereign infrastructure organism for the post-quantum internet." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "Organization", name: "DrkLynX", url: "https://drklynx.com", description: "Sovereign infrastructure for the post-quantum internet." })}</script>
      </Helmet>

      {/* §3.1.2 — Hero */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* WebGL lattice with SVG fallback — ticker always loads first */}
        <HeroBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <h1 data-testid="hero-headline" className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05] text-signal mb-6">
              Your Digital Future,<br /><span className="text-green">Quantum-Proof.</span>
            </h1>
            <p className="text-lg sm:text-xl text-steel leading-relaxed max-w-2xl mb-10">
              Sovereign hosting, end-to-end <span className="text-green font-semibold">Vicode</span><span className="text-steel-muted text-sm"> (Verifiable-Code — every commit cryptographically attested at build time)</span>, and cryptographic truth for the post-quantum internet. Operated by a sovereign architect, powered by ~300,000 lines of agentic code.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-2">
              <Link to="/pricing"><Button size="lg" data-testid="hero-cta-primary">Start Free Tier <ArrowRight className="w-4 h-4" /></Button></Link>
              {/*
                Tactical-honesty wire (2026-06-21): point the verify CTA at a real,
                third-party-readable SPS attestation receipt — drklynx-auth PR #2
                run 27841266283 (HMAC seal f513bd3a25e234ec568bd001..., 7 CRITICAL
                findings caught + sealed). Better to land a transparent external
                link tonight than a polished internal page with vapor inside.
                /ledger-pulse polished surface ships next session per Phase 2.
              */}
              <a
                href="https://github.com/NoirLynX-Intelligence/drklynx-auth/actions/runs/27841266283"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-secondary-wrapper"
              >
                <Button variant="secondary" size="lg" data-testid="hero-cta-secondary">Verify Our Attestation</Button>
              </a>
            </div>
            <p className="text-xs text-steel-muted font-mono mb-10 max-w-2xl">
              Latest sealed run: SPS v2.0.12 · 7-sentinel panel · HMAC-SHA256
              attestation <span className="text-cyan">f513bd3a…</span> · 7 production-risk findings caught
            </p>
            {/* Live counters — every value here is independently verifiable.
                Audited 2026-06-22 (M10) — replaced "7 Sovereign Edge Quorum Nodes"
                (no BFT impl) with the SPS sentinel-lens count (which IS live).
                "1,500+" → "1,390" (the real CF zone count). PQC trajectory unchanged. */}
            <div data-testid="hero-counters" className="flex flex-wrap gap-8 pt-8 border-t border-signal/10">
              <StatCounter value="7" label="SPS Adversarial Review Lenses" icon={Globe} />
              <StatCounter value="1,390" label="Sovereign Zones on Cloudflare" icon={Activity} accent="violet" />
              <StatCounter value="Phase A → B" label="Post-Quantum Ready Architecture" icon={Shield} accent="gold" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-obsidian to-transparent" />
      </section>

      {/* §3.1.3 — Product Matrix Bento Grid */}
      <section data-testid="product-matrix" className="py-20 lg:py-28 border-t border-signal/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureTile icon={Server} title="DrkLynX Hosting" description="Managed hosting, cryptographically sealed. Shared, VPS, Reseller, Dedicated." href="/products/hosting" accent="cyan" wide className="lg:col-span-2" />
            <FeatureTile icon={Code} title="DrkLynX Weaver" description="QDRC Vicoder Engine. Describe, build, ship." href="/products/weaver" accent="violet" />
            <FeatureTile icon={Users} title="ZbraLynX" description="Enterprise CRM with E2E PQC encryption." href="/products/zbralynx" accent="violet" />
            <FeatureTile icon={Rocket} title="DrkLynX Crossing" description="One-click migration from legacy SaaS. Walk out with your data." href="/products/crossing" accent="cyan" wide className="lg:col-span-2" />
          </div>
        </div>
      </section>

      {/* §3.1.4 — Live Ledger Pulse Stripe */}
      <section data-testid="ledger-stripe" className="py-16 border-t border-signal/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-green" />
              <h2 className="font-heading font-bold text-lg text-signal">Ledger Pulse</h2>
              <Badge variant="cyan">Live</Badge>
            </div>
            <Link to="/ledger-pulse" className="text-sm text-cyan hover:text-signal transition-colors duration-fast flex items-center gap-1">
              View All <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
            {mockSeals.map((seal, i) => (
              <LedgerCard key={seal.hash} seal={seal} newest={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* §3.1.5 — Why DrkLynX Comparison */}
      <section data-testid="why-comparison" className="py-20 lg:py-28 border-t border-signal/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-signal mb-4 max-w-3xl">
            The only stack where hosting, code, data, and comms are cryptographically sealed to you — not leased from a vendor.
          </h2>
          <p className="text-steel mb-12 max-w-2xl">Three fundamentally different approaches to infrastructure security.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Legacy Clouds', subtitle: 'AWS, GCP, Azure', items: ['Vendor-owned encryption keys', 'Classical TLS only', 'Data model = vendor lock-in'], color: 'border-steel-muted/30' },
              { title: 'Reseller Hosts', subtitle: 'Hostinger, Bluehost', items: ['Third-party registrar friction', 'Classical TLS only', 'Migration = pain'], color: 'border-steel-muted/30' },
              { title: 'DrkLynX', subtitle: 'Sovereign Stack', items: ['You own the key, we own the math', 'Post-Quantum Ready · Phase A live, Phase B ML-DSA-65 in flight', 'DrkLynX Crossing = one-click liberation'], color: 'border-green/40 ring-1 ring-green/10' },
            ].map((col) => (
              <div key={col.title} className={`border rounded-lg bg-obsidian-elevated p-6 ${col.color}`}>
                <div className="font-heading font-bold text-signal mb-1">{col.title}</div>
                <div className="font-mono text-[10px] text-steel-muted uppercase tracking-wider mb-4">{col.subtitle}</div>
                <ul className="space-y-3">
                  {col.items.map((item) => (
                    <li key={item} className="text-steel text-sm flex items-start gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${col.title === 'DrkLynX' ? 'bg-green' : 'bg-steel-muted'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §3.1.6 — Exodus Refugee Capture Block */}
      <section data-testid="exodus-capture" className="py-20 bg-gradient-to-r from-violet/10 via-violet/5 to-transparent border-y border-violet/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-signal mb-4">Trapped in a legacy SaaS?</h2>
          <p className="text-steel text-lg mb-10">Walk out with your data. Zero lock-in, zero data loss, cryptographic proof of integrity.</p>
          <div className="flex flex-wrap justify-center gap-8 mb-10">
            {[
              { from: 'Wix', to: 'DrkLynX Hosting' },
              { from: 'Mailchimp', to: 'DrkLynX Signal' },
              { from: 'HubSpot', to: 'ZbraLynX' },
            ].map((m) => (
              <div key={m.from} className="flex items-center gap-3 text-sm">
                <span className="text-steel-muted line-through">{m.from}</span>
                <ArrowRight className="w-4 h-4 text-violet" />
                <span className="text-violet font-medium">{m.to}</span>
              </div>
            ))}
          </div>
          <Link to="/products/crossing"><Button variant="violet" size="lg">Start Your Crossing <ArrowRight className="w-4 h-4" /></Button></Link>
        </div>
      </section>

      {/* §3.1.7 — Standards & Integrations Ribbon (honest infrastructure attribution, not fake customer logos) */}
      <section data-testid="standards-ribbon" className="py-12 border-b border-signal/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-mono text-[10px] text-steel-muted uppercase tracking-wider mb-6">Built on open standards · Aligned to regulatory mandates</p>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
            {[
              { label: 'Cloudflare Workers', tone: 'edge runtime' },
              { label: 'NIST FIPS 203 / 204', tone: 'PQC standards' },
              { label: 'W3C Verifiable Credentials', tone: 'eIDAS 2.0 alignment' },
              { label: 'EU AI Act Article 50', tone: 'AI transparency' },
              { label: 'GDPR · CCPA', tone: 'privacy compliance' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1 px-3 py-2 border border-signal/10 rounded">
                <span className="text-xs text-steel font-mono">{s.label}</span>
                <span className="text-[9px] text-steel-muted uppercase tracking-wider">{s.tone}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §3.1.8 — Developer Footer CTA */}
      <section data-testid="dev-cta" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-signal mb-3">Ship in 60 seconds</h2>
          <p className="text-steel mb-8">One command. PQC-hardened from the first byte.</p>
          <div className="inline-flex items-center gap-3 bg-obsidian-elevated border border-signal/10 rounded-lg px-6 py-4 mb-6">
            <code className="font-mono text-cyan text-sm sm:text-base">npx drklynx init my-project</code>
            <button onClick={copyCmd} className="text-steel-muted hover:text-cyan transition-colors duration-fast" aria-label="Copy command">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {copied && <p className="text-success text-xs font-mono">Copied to clipboard</p>}
          <div className="mt-6">
            <Link to="/docs/getting-started" className="text-sm text-cyan hover:text-signal transition-colors duration-fast">
              Read the docs <ArrowRight className="w-3.5 h-3.5 inline" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default HomePage;
